import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2 } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";

const DEFAULT_ENDPOINT = "/partner/uploads/property-image";
// Backend origin, so uploaded /api/uploads/… URLs resolve on any page.
const BACKEND_ORIGIN = API.replace(/\/api\/?$/, "");
const toAbsUrl = (u) => (!u ? "" : /^https?:/i.test(u) ? u : `${BACKEND_ORIGIN}${u.startsWith("/") ? "" : "/"}${u}`);

// Compresses in-browser (max 2000px) then uploads to object storage; returns an absolute URL.
export async function uploadImage(file, endpoint = DEFAULT_ENDPOINT) {
    const optimised = await compressImage(file);
    const fd = new FormData();
    fd.append("file", optimised);
    const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return toAbsUrl(r.data.url);
}

/* ---------- Image upload widgets ---------- */

export function ImageUploader({ label, value, onChange, testid, hint, endpoint = DEFAULT_ENDPOINT }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const url = await uploadImage(file, endpoint);
            onChange(url);
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            {value ? (
                <div className="relative inline-block group border border-black/15 bg-black/5">
                    <img src={value} alt="cover" className="max-h-56 max-w-full object-contain block"/>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            data-testid={`${testid}-replace`}
                            className="inline-flex items-center gap-2 bg-[var(--ebg-gold)] text-black text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-2 disabled:opacity-60"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
                            Replace
                        </button>
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            data-testid={`${testid}-remove`}
                            className="inline-flex items-center gap-2 bg-white text-black text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-2"
                        >
                            <X size={13}/> Remove
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid={`${testid}-pick`}
                    className="w-full md:w-96 h-40 border-2 border-dashed border-black/25 hover:border-[var(--ebg-gold)] bg-black/[0.02] flex flex-col items-center justify-center text-center text-black/55 hover:text-black transition-colors disabled:opacity-60"
                >
                    {busy ? <Loader2 size={22} className="animate-spin mb-2"/> : <Upload size={22} className="mb-2 text-[var(--ebg-gold)]"/>}
                    <span className="text-[12px] uppercase tracking-[0.22em] font-bold">
                        {busy ? "Uploading…" : "Click to upload image"}
                    </span>
                    {hint && <span className="text-[11px] text-black/50 mt-1 max-w-xs">{hint}</span>}
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                data-testid={`${testid}-input`}
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
        </div>
    );
}

export function GalleryUploader({ label, values, onChange, max = 20, testid, endpoint = DEFAULT_ENDPOINT }) {
    const inputRef = useRef(null);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);

    const handleFiles = async (files) => {
        const arr = Array.from(files || []);
        if (arr.length === 0) return;
        const remaining = Math.max(0, max - (values?.length || 0));
        if (remaining === 0) {
            toast.error(`Maximum ${max} images.`);
            return;
        }
        const toUpload = arr.slice(0, remaining);
        if (arr.length > remaining) {
            toast.warning(`Only the first ${remaining} images will be uploaded (max ${max} total).`);
        }
        setUploadingCount(toUpload.length);
        const uploaded = [];
        for (const file of toUpload) {
            try {
                const url = await uploadImage(file, endpoint);
                uploaded.push(url);
            } catch (e) {
                toast.error(`${file.name}: ${formatApiError(e)}`);
            }
        }
        if (uploaded.length > 0) {
            onChange([...(values || []), ...uploaded]);
            toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
        }
        setUploadingCount(0);
        if (inputRef.current) inputRef.current.value = "";
    };

    const removeAt = (i) => onChange((values || []).filter((_, idx) => idx !== i));

    // Reorder helper — move item at `from` to position `to`
    const reorder = (from, to) => {
        if (from === to || from == null || to == null) return;
        const list = [...(values || [])];
        const [moved] = list.splice(from, 1);
        list.splice(to, 0, moved);
        onChange(list);
    };

    const canAdd = (values?.length || 0) < max;

    return (
        <div data-testid={testid}>
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55">{label}</div>
                <div className="text-[10px] text-black/45 tabular-nums">{values?.length || 0} / {max}</div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {(values || []).map((url, i) => (
                    <div
                        key={`${url}-${i}`}
                        draggable
                        onDragStart={(e) => {
                            setDragIdx(i);
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", String(i));
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overIdx !== i) setOverIdx(i); }}
                        onDragEnter={(e) => { e.preventDefault(); setOverIdx(i); }}
                        onDragLeave={() => setOverIdx((v) => (v === i ? null : v))}
                        onDrop={(e) => { e.preventDefault(); const from = Number(e.dataTransfer.getData("text/plain")); reorder(from, i); setDragIdx(null); setOverIdx(null); }}
                        onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                        className={`relative group aspect-square bg-black/5 border overflow-hidden cursor-move select-none transition-all ${
                            dragIdx === i ? "opacity-40 scale-95" : "opacity-100"
                        } ${overIdx === i && dragIdx !== i ? "border-[var(--ebg-gold)] ring-2 ring-[var(--ebg-gold)]/40" : "border-black/10"}`}
                        data-testid={`${testid}-thumb-${i}`}
                    >
                        <img src={url} alt={`gallery ${i + 1}`} className="w-full h-full object-cover pointer-events-none" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}/>
                        {/* position marker (helpful when reordering) */}
                        <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-black/75 text-white text-[10px] font-bold flex items-center justify-center tabular-nums pointer-events-none">
                            {i + 1}
                        </div>
                        {/* Cover-photo pill on the first thumbnail */}
                        {i === 0 && (
                            <div className="absolute bottom-1.5 left-1.5 bg-[var(--ebg-gold)] text-black text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 pointer-events-none">
                                Featured
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => removeAt(i)}
                            data-testid={`${testid}-remove-${i}`}
                            className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                            title="Remove"
                        >
                            <X size={14}/>
                        </button>
                    </div>
                ))}
                {canAdd && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploadingCount > 0}
                        data-testid={`${testid}-add`}
                        className="aspect-square border-2 border-dashed border-black/25 hover:border-[var(--ebg-gold)] bg-black/[0.02] flex flex-col items-center justify-center text-black/55 hover:text-black transition-colors disabled:opacity-60"
                    >
                        {uploadingCount > 0 ? (
                            <>
                                <Loader2 size={20} className="animate-spin mb-1"/>
                                <span className="text-[10px] uppercase tracking-[0.18em] font-bold">Uploading {uploadingCount}…</span>
                            </>
                        ) : (
                            <>
                                <Upload size={20} className="text-[var(--ebg-gold)] mb-1"/>
                                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-center">Add Images</span>
                            </>
                        )}
                    </button>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                data-testid={`${testid}-input`}
                onChange={(e) => handleFiles(e.target.files)}
            />
            <p className="text-[11px] text-black/50 mt-2">
                JPG/PNG/WEBP up to 8 MB each. <b>Drag to reorder</b> &mdash; the first image becomes the listing&rsquo;s featured photo.
            </p>
        </div>
    );
}

